<template>
  <AnalyticsWidget
    title="Summary Ratios"
    subTitle="All summary ratios from various modules"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    >
    <div class="p-2">
      <LoadingPanel :loading="loading" />
      <div v-if="!loading">
        <AnalyticsDashboardValueRow v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder) && orderBreakdownData.day">
          <div class="col">
            <AnalyticsDashboardValue
              title="Today's Average Panel Sale Price"
              :value="calculateAveragePanelSalePrice(orderBreakdownData.day.current)"
              :previous="calculateAveragePanelSalePrice(orderBreakdownData.day.previous)"
              helpTip="Compared to previous working day">
              {{formatCurrency(calculateAveragePanelSalePrice(orderBreakdownData.day.current))}}
              <template v-slot:info>Average Across {{orderBreakdownData.day.current.panelCount + orderBreakdownData.day.current.stockSheetCount}} Panels/Sheets in {{orderBreakdownData.day.current.orderCount}} Orders Today</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="Today's Average Panel Area"
              :value="calculateAveragePanelArea(orderBreakdownData.day.current)"
              :previous="calculateAveragePanelArea(orderBreakdownData.day.previous)"
              helpTip="Compared to previous working day">
              {{formatDecimal(calculateAveragePanelArea(orderBreakdownData.day.current))}}m&sup2;
              <template v-slot:info>Average Across {{orderBreakdownData.day.current.panelCount + orderBreakdownData.day.current.stockSheetCount}} Panels/Sheets in {{orderBreakdownData.day.current.orderCount}} Orders Today</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="Today's Average Panel Cost Price"
              :value="calculateAveragePanelCostPrice(orderBreakdownData.day.current)"
              :previous="calculateAveragePanelCostPrice(orderBreakdownData.day.previous)"
              helpTip="Compared to previous working day">
              {{formatCurrency(calculateAveragePanelCostPrice(orderBreakdownData.day.current))}}
              <template v-slot:info>Average Across {{orderBreakdownData.day.current.panelCount + orderBreakdownData.day.current.stockSheetCount}} Panels/Sheets in {{orderBreakdownData.day.current.orderCount}} Orders Today</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="Today's Average Order Value"
              :value="calculateAverageOrderValue(orderBreakdownData.day.current)"
              :previous="calculateAverageOrderValue(orderBreakdownData.day.previous)"
              helpTip="Compared to previous working day">
              {{formatCurrency(calculateAverageOrderValue(orderBreakdownData.day.current))}}
              <template v-slot:info>Average Across {{orderBreakdownData.day.current.orderCount}} Orders Today</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        
        <AnalyticsDashboardValueRow v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceOrder) && orderBreakdownData.day">
          <div class="col">
            <AnalyticsDashboardValue
              title="30 Day Average Panel Sale Price"
              :value="calculateAveragePanelSalePrice(orderBreakdownData.rolling30.current)"
              :previous="calculateAveragePanelSalePrice(orderBreakdownData.rolling30.previous)"
              helpTip="Average over last 30 days compared to last 30-60 days">
              {{formatCurrency(calculateAveragePanelSalePrice(orderBreakdownData.rolling30.current))}}
              <template v-slot:info>Average Across {{orderBreakdownData.rolling30.current.panelCount + orderBreakdownData.rolling30.current.stockSheetCount}} Panels/Sheets in {{orderBreakdownData.rolling30.current.orderCount}} Orders Over Last 30 days</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="30 Day Average Panel Area"
              :value="calculateAveragePanelArea(orderBreakdownData.rolling30.current)"
              :previous="calculateAveragePanelArea(orderBreakdownData.rolling30.previous)"
              helpTip="Average over last 30 days compared to last 30-60 days">
              {{formatDecimal(calculateAveragePanelArea(orderBreakdownData.rolling30.current))}}m&sup2;
              <template v-slot:info>Average Across {{orderBreakdownData.rolling30.current.panelCount + orderBreakdownData.rolling30.current.stockSheetCount}} Panels/Sheets in {{orderBreakdownData.rolling30.current.orderCount}} Orders Over Last 30 days</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="30 Day Average Panel Cost Price"
              :value="calculateAveragePanelCostPrice(orderBreakdownData.rolling30.current)"
              :previous="calculateAveragePanelCostPrice(orderBreakdownData.rolling30.previous)"
              helpTip="Average over last 30 days compared to last 30-60 days">
              {{formatCurrency(calculateAveragePanelCostPrice(orderBreakdownData.rolling30.current))}}
              <template v-slot:info>Average Across {{orderBreakdownData.rolling30.current.panelCount + orderBreakdownData.rolling30.current.stockSheetCount}} Panels/Sheets in {{orderBreakdownData.rolling30.current.orderCount}} Orders Over Last 30 days</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="30 Day Average Order Value"
              :value="calculateAverageOrderValue(orderBreakdownData.rolling30.current)"
              :previous="calculateAverageOrderValue(orderBreakdownData.rolling30.previous)"
              helpTip="Average over last 30 days compared to last 30-60 days">
              {{formatCurrency(calculateAverageOrderValue(orderBreakdownData.rolling30.current))}}
              <template v-slot:info>Average Across {{orderBreakdownData.rolling30.current.orderCount}} Orders Over Last 30 days</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow v-if="userHasReadPermission(PermissionTypes.IntelligencePermissionTypes.intelligenceAccount) && invoiceBreakdownData.day">
          <div class="col">
            <AnalyticsDashboardValue
              title="Today's Average Invoice Value"
              :value="calculateAverageInvoiceValue(invoiceBreakdownData.day.current)"
              :previous="calculateAverageInvoiceValue(invoiceBreakdownData.day.previous)"
              helpTip="Compared to previous working day">
              {{formatCurrency(calculateAverageInvoiceValue(invoiceBreakdownData.day.current))}}
              <template v-slot:info>Average Across {{invoiceBreakdownData.day.current.invoiceCount}} Invoices Today</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
            <AnalyticsDashboardValue
              title="30 Day Average Invoice Value"
              :value="calculateAverageInvoiceValue(invoiceBreakdownData.rolling30.current)"
              :previous="calculateAverageInvoiceValue(invoiceBreakdownData.rolling30.previous)"
              helpTip="Average over last 30 days compared to last 30-60 days">
              {{formatCurrency(calculateAverageInvoiceValue(invoiceBreakdownData.rolling30.current))}}
              <template v-slot:info>Average Across {{invoiceBreakdownData.rolling30.current.invoiceCount}} Invoices Over Last 30 days</template>
            </AnalyticsDashboardValue>
          </div>
          <div class="col">
          </div>
          <div class="col">
          </div>
        </AnalyticsDashboardValueRow>
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'

export default {
  name: 'SalesSummaryWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValue,
    AnalyticsDashboardValueRow
  },
  props: {
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  data() {
    return {
      loading: true,
      orderBreakdownData: {},
      invoiceBreakdownData: {}
    }
  },
  watch: {
    contactId: function() {
      this.requestData()
    },
    filter: function() {
      this.requestData()
    }
  },
  mounted() {
    this.loading = true
    this.requestData()

    this.subscribeEndpoint('/accounts/invoices', () => {
      this.requestData()
    })
    this.subscribeEndpoint('/orders', () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/accounts/invoices')
    this.unsubscribeEndpoint('/orders')
  },
  methods: {
    requestData() {
      var params = {
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      this.$apiService.analyticsService.getOrdersValueBreakdown(params)
      .then(response => {
        this.orderBreakdownData = response.data
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })

      this.$apiService.analyticsService.getInvoiceValueBreakdown(params)
      .then(response => {
        this.invoiceBreakdownData = response.data
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    calculateAveragePanelSalePrice(breakdownInfo) {
      var currentTotalItems = breakdownInfo.panelCount + breakdownInfo.stockSheetCount
      if(currentTotalItems > 0)
        return breakdownInfo.orderValue / currentTotalItems
      
      return 0
    },
    calculateAveragePanelCostPrice(breakdownInfo) {
      var currentTotalItems = breakdownInfo.panelCount + breakdownInfo.stockSheetCount
      if(currentTotalItems > 0)
        return (breakdownInfo.panelCOG + breakdownInfo.sheetCOG) / currentTotalItems

      return 0
    },
    calculateAveragePanelArea(breakdownInfo) {
      var currentTotalItems = breakdownInfo.panelCount + breakdownInfo.stockSheetCount
      if(currentTotalItems > 0)
        return (breakdownInfo.panelArea + breakdownInfo.stockSheetArea) / currentTotalItems
      
      return 0
    },
    calculateAverageOrderValue(breakdownInfo) {
      if(breakdownInfo.orderCount > 0)
        return breakdownInfo.orderValue / breakdownInfo.orderCount
      return 0
    },
    calculateAverageInvoiceValue(breakdownInfo) {
      if(breakdownInfo.invoiceCount > 0)
        return breakdownInfo.invoiceValue / breakdownInfo.invoiceCount
      return 0
    }
  }
}
</script>